/**
 * PAYMENT PROCESSORS — platform-level processor credentials (Super Admin only).
 * Today: MagTek UniGate and SUNBAY Nexus, the platform default every location uses unless it has
 * its own account. Stored in SSM (SecureString) by super-admin-ext3; this screen
 * shows status only and edits write-only.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { KeyRound, Pencil, Trash2 } from 'lucide-react';
import { api } from '../../services/apiService';
import { toastService } from '../../services/toastService';
import { RubyLoader } from '../../components/RubyLoader';
import { UnigateCredentialsModal, ConfirmLight } from '../../components/pos/unigate/UnigateCredentialsModal';
import { platformChip } from '../../components/pos/unigate/unigateCreds';
import { SunbayCredentialsModal } from '../../components/pos/sunbay/SunbayCredentialsModal';
import { sunbayChip, sunbayStatusLine } from '../../components/pos/sunbay/sunbayCreds';

const fmt = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

/** SUNBAY Nexus platform credentials row. */
const SunbayRow: React.FC = () => {
  const [st, setSt] = useState<any | null | undefined>(undefined);
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    setSt(undefined);
    api.posNmi.getSunbayNexusPlatform().then((s: any) => setSt(s || null)).catch(() => setSt(null));
  }, []);
  useEffect(() => { load(); }, [load]);
  const chip = sunbayChip(st);
  const remove = async () => {
    setBusy(true);
    try {
      await api.posNmi.deleteSunbayNexusPlatform();
      toastService.success('SUNBAY credentials removed.');
      setRemoving(false);
      load();
    } catch (e: any) {
      toastService.error(e?.serverMessage || e?.message || 'Could not remove the credentials.');
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-stone-200 bg-white" data-testid="pp-sunbay">
      <div className="flex flex-wrap items-center gap-3 px-5 py-4">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[#FFF1F3] text-ruby"><KeyRound size={18} /></span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[14px] font-semibold">
            SUNBAY Nexus
            {st !== undefined && (
              <span data-testid="pp-sunbay-chip" className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${chip.tone === 'ok' ? 'bg-emerald-50 text-emerald-700' : 'bg-[#FFF1F3] text-ruby-deep'}`}>{chip.text}</span>
            )}
          </div>
          <div className="text-[12px] text-stone-500" data-testid="pp-sunbay-line">
            CPad Pay on SUNMI tills · SUNBAY Tapro
            {st?.set && <> · {sunbayStatusLine(st, fmt)}</>}
            {st === null && <> · could not load the status</>}
          </div>
        </div>
        {st === undefined ? <RubyLoader size={24} /> : (
          <>
            <button type="button" onClick={() => setEditing(true)} data-testid="pp-sunbay-edit"
              className="inline-flex min-h-[44px] items-center gap-1 rounded-lg bg-black px-4 text-sm font-semibold text-white hover:bg-stone-800">
              <Pencil size={14} /> {st?.set ? 'Replace' : 'Set credentials'}
            </button>
            {st?.set && (
              <button type="button" onClick={() => setRemoving(true)} data-testid="pp-sunbay-remove"
                className="inline-flex min-h-[44px] items-center gap-1 rounded-lg px-3 text-sm font-semibold text-ruby hover:bg-[#FFF1F3]">
                <Trash2 size={14} /> Remove
              </button>
            )}
          </>
        )}
      </div>
      {editing && (
        <SunbayCredentialsModal
          onSave={async (body) => { await api.posNmi.putSunbayNexusPlatform(body); toastService.success('SUNBAY credentials saved.'); load(); }}
          onClose={() => setEditing(false)} />
      )}
      {removing && (
        <ConfirmLight title="Remove SUNBAY credentials?" confirmLabel="Remove" busy={busy}
          message="Tills with a SUNBAY CPad Pay will stop taking card payments until new credentials are set."
          onConfirm={remove} onCancel={() => setRemoving(false)} />
      )}
    </div>
  );
};

export const PaymentProcessorsPage: React.FC = () => {
  const [st, setSt] = useState<any | null | undefined>(undefined);
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    setSt(undefined);
    api.posNmi.getMagtekUnigatePlatform().then((s: any) => setSt(s || null)).catch(() => setSt(null));
  }, []);
  useEffect(() => { load(); }, [load]);
  const chip = platformChip(st);

  const remove = async () => {
    setBusy(true);
    try {
      await api.posNmi.deleteMagtekUnigatePlatform();
      toastService.success('Platform UniGate credentials removed.');
      setRemoving(false);
      load();
    } catch (e: any) {
      toastService.error(e?.serverMessage || e?.message || 'Could not remove the credentials.');
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-6 text-stone-900">
      <div>
        <h1 className="text-xl font-semibold">Payment processors</h1>
        <p className="text-sm text-stone-500">Platform credentials used by every location unless it has its own account.</p>
      </div>
      <div className="rounded-xl border border-stone-200 bg-white" data-testid="pp-magtek">
        <div className="flex flex-wrap items-center gap-3 px-5 py-4">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[#FFF1F3] text-ruby"><KeyRound size={18} /></span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-[14px] font-semibold">
              MagTek UniGate
              {st !== undefined && (
                <span data-testid="pp-chip" className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${chip.tone === 'ok' ? 'bg-emerald-50 text-emerald-700' : 'bg-[#FFF1F3] text-ruby-deep'}`}>{chip.text}</span>
              )}
            </div>
            <div className="text-[12px] text-stone-500">
              DynaFlex II readers on SUNMI tills · Magensa UniGate
              {st?.set && <> · {st.baseUrlHost || '—'} · updated {fmt(st.updatedAt)}{st.updatedBy ? ` by ${st.updatedBy}` : ''}</>}
              {st && <span data-testid="pp-te"> · Website key: {st.teconnect?.publicKey ? `Set · ${st.teconnect.environment === 'production' ? 'Production' : 'Sandbox'}` : 'Missing'}</span>}
              {st === null && <> · could not load the status</>}
            </div>
          </div>
          {st === undefined ? <RubyLoader size={24} /> : (
            <>
              <button type="button" onClick={() => setEditing(true)} data-testid="pp-edit"
                className="inline-flex min-h-[44px] items-center gap-1 rounded-lg bg-black px-4 text-sm font-semibold text-white hover:bg-stone-800">
                <Pencil size={14} /> {st?.set ? 'Replace' : 'Set credentials'}
              </button>
              {st?.set && (
                <button type="button" onClick={() => setRemoving(true)} data-testid="pp-remove"
                  className="inline-flex min-h-[44px] items-center gap-1 rounded-lg px-3 text-sm font-semibold text-ruby hover:bg-[#FFF1F3]">
                  <Trash2 size={14} /> Remove
                </button>
              )}
            </>
          )}
        </div>
      </div>
      <SunbayRow />
      {editing && (
        <UnigateCredentialsModal title="Platform UniGate credentials" scope="Platform default" withTeconnect teconnect={st?.teconnect}
          onSave={async (body) => { await api.posNmi.putMagtekUnigatePlatform(body); toastService.success('Platform UniGate credentials saved.'); load(); }}
          onClose={() => setEditing(false)} />
      )}
      {removing && (
        <ConfirmLight title="Remove platform credentials?" confirmLabel="Remove" busy={busy}
          message="Locations without their own UniGate account will stop taking MagTek card payments until new credentials are set."
          onConfirm={remove} onCancel={() => setRemoving(false)} />
      )}
    </div>
  );
};

export default PaymentProcessorsPage;
